import { supabase } from "@/integrations/supabase/client";
import {
  roomAccessFor,
  type RoomAccess,
  type RoomRecord,
} from "@/engine/state/roomAccess";
import { RoomModeSchema, type RoomMode } from "@/engine/state/roomControl";
import { isRoomCode } from "@/engine/state/roomIdentity";
import {
  DEFAULT_ROOM_CONFIG,
  roomConfigFromRow,
  type RoomConfig,
  type RoomConfigColumns,
} from "@/engine/state/roomConfig";

/**
 * The note beside a room: writing it, reading it, and keeping it warm.
 *
 * A room is still a realtime channel named after its code; the row says the
 * code was created, what kind of room it is and when somebody was last
 * standing in it. Creating never consults it, joining does, and a failed read
 * lets the visitor in — a backend hiccup must refuse nobody.
 *
 * The narrow client shape keeps query-builder details at the transport seam
 * and makes this persistence boundary independently testable.
 */

/**
 * How a write to the room's row came out.
 *
 * `taken` is the duplicate: the upsert carries `ignoreDuplicates`, so a code
 * that is already somebody's room comes back with no error and no row. It used
 * to read as `recorded`, and the visitor who pressed Create walked into the
 * other room (#764); the press draws another code instead. `not-attempted` is a
 * write this module declined to make. `refused` is a write that was made and came back rejected — a
 * constraint it violates, a policy that forbids it — and it is the only one of
 * the three worth telling the host about.
 *
 * Not split further by Postgres code on purpose. A check violation today and a
 * policy refusal tomorrow leave the host the same single thing to do, and a
 * taxonomy of error codes at the transport seam would be a second copy of the
 * schema, kept in step by hand.
 */
export type RoomRecordOutcome =
  "recorded" | "taken" | "not-attempted" | "refused";

interface RoomRow {
  code: string;
  is_permanent?: boolean | null;
  last_seen_at?: string | null;
  created_by?: string | null;
  account_owned?: boolean | null;
  is_owner?: boolean | null;
  host_peer_id?: string | null;
  room_mode?: string | null;
  display_name?: string | null;
  autostart_tour?: boolean | null;
  journal_visible_to_all?: boolean | null;
  voice_cleaning?: boolean | null;
  video_transport?: string | null;
  audio_transport?: string | null;
}

function roomFromPayload(payload: unknown): RoomRow | null | undefined {
  if (!payload || typeof payload !== "object" || !("room" in payload)) {
    return undefined;
  }
  const room = Reflect.get(payload, "room");
  if (!room || typeof room !== "object") return null;
  const code = Reflect.get(room, "code");
  return typeof code === "string" ? { ...room, code } : undefined;
}

export interface RoomRecordWriter {
  /**
   * The room behind a code. `asAccount: false` asks without the visitor's
   * session, for a read whose answer does not depend on who is asking.
   */
  lookup(
    code: string,
    options?: { asAccount?: boolean },
  ): PromiseLike<{
    data: RoomRow | null;
    error: { message: string } | null;
  }>;
  from(table: string): {
    upsert(
      values: Record<string, unknown>,
      options: { onConflict: string; ignoreDuplicates: boolean },
    ): {
      select(columns: string): PromiseLike<{
        data?: { code: string }[] | null;
        error: { message: string } | null;
      }>;
    };
    update(
      values: Record<string, unknown>,
      options?: { count: "exact" },
    ): {
      eq(
        column: string,
        value: string,
      ): PromiseLike<{
        error: { message: string } | null;
        count?: number | null;
      }>;
    };
    delete(options: { count: "exact" }): {
      eq(
        column: string,
        value: string,
      ): PromiseLike<{
        error: { message: string } | null;
        count?: number | null;
      }>;
    };
    select(columns: string): {
      eq(
        column: string,
        value: string,
      ): {
        order(
          column: string,
          options: { ascending: boolean; nullsFirst: boolean },
        ): {
          limit(count: number): PromiseLike<{
            data: RoomRow[] | null;
            error: { message: string } | null;
          }>;
        };
      };
    };
  };
  rpc(
    fn: string,
    args: Record<string, unknown>,
  ): PromiseLike<{ error: { message: string } | null }>;
}

/**
 * Test seam: the browser client, seen through the little of it used here.
 *
 * The narrowing happens here, in one place, rather than at every call site.
 */
export const defaultRoomRecordWriter = (): RoomRecordWriter => {
  const client: unknown = supabase;
  /* eslint-disable-next-line @typescript-eslint/consistent-type-assertions --
     the one seam between the Supabase client and the narrow shape above */
  const writer = client as RoomRecordWriter;
  return {
    from: writer.from.bind(writer),
    rpc: writer.rpc.bind(writer),
    async lookup(code, { asAccount = true } = {}) {
      const token = asAccount
        ? (await supabase.auth.getSession()).data.session?.access_token
        : undefined;
      const response = await fetch("/api/room-lookup", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ code }),
      });
      if (!response.ok) {
        return lookupFailed(`${response.status} ${response.statusText}`);
      }
      const payload: unknown = await response.json();
      const room = roomFromPayload(payload);
      if (room === undefined) {
        return lookupFailed("invalid room response");
      }
      return { data: room, error: null };
    },
  };
};

/**
 * A lookup that could not be made, said out loud.
 *
 * Every reader of this answer falls open — the access check lets the visitor
 * in, the configuration takes its defaults — so a route that is down costs
 * nobody their room and tells nobody it is down. #616 was exactly that: the
 * key the route needs was missing from the local stack for two commits, every
 * mistyped code opened a room, and the only symptom was one e2e spec. The warn
 * is the symptom the silence was missing.
 */
function lookupFailed(message: string) {
  console.warn(`[room_lookup] /api/room-lookup unavailable: ${message}`);
  return { data: null, error: { message } };
}

export interface RecordRoomOptions {
  /** The memorable code the room was created under. */
  code: string;
  /** The creator's account. A room with nobody behind it is not recorded. */
  ownerId?: string | null;
  /** What kind of room the host opened it as. */
  roomMode?: RoomMode;
  /**
   * What the host called it. Blank is no name, and the room is called by its
   * mode; the column holds one to sixty characters once trimmed.
   */
  displayName?: string | undefined;
  writer?: RoomRecordWriter;
}

/** The name as the column will take it, or null for none. */
export function recordedName(name: string | undefined): string | null {
  const trimmed = name?.trim() ?? "";
  return trimmed ? trimmed.slice(0, 60) : null;
}

/**
 * Writes the room's row, and resolves either way with how it came out.
 *
 * Two people can create the same code at once, and a visitor can recreate a
 * code they used yesterday — both are ordinary, so a duplicate is success and
 * not an error worth telling anybody about.
 *
 * Every room has an owner: the column is not null, and an anonymous insert is
 * refused by the policy anyway, so an ownerless attempt is dropped here rather
 * than sent to be rejected.
 *
 * Nothing here waits for the room: entry is already under way, and the outcome
 * is read afterwards by whoever can act on it.
 */
export async function recordRoom({
  code,
  ownerId = null,
  roomMode = "campfire",
  displayName,
  writer = defaultRoomRecordWriter(),
}: RecordRoomOptions): Promise<RoomRecordOutcome> {
  const trimmed = normalise(code);
  // A row under something that is not a room code would record a room no
  // visitor can be let into (#693).
  if (!isRoomCode(trimmed) || !ownerId) return "not-attempted";
  try {
    const { data, error } = await writer
      .from("rooms")
      .upsert(
        {
          code: trimmed,
          created_by: ownerId,
          room_mode: roomMode,
          display_name: recordedName(displayName),
        },
        { onConflict: "code", ignoreDuplicates: true },
      )
      // The row comes back only where it was written: a duplicate that was
      // skipped answers with none, and that is a code already in use.
      .select("code");
    if (!error)
      return Array.isArray(data) && data.length === 0 ? "taken" : "recorded";
    return "refused";
  } catch {
    // A request that threw may or may not have landed. Warning about a room
    // that was in fact recorded is the cheaper mistake: the host shares the
    // code, it works, and they are out a sentence — where staying silent about
    // a room that was not recorded is how this was found in the first place.
    return "refused";
  }
}

const normalise = (code: string) => code.trim().toLowerCase();

export interface LookupRoomOptions {
  code: string;
  writer?: RoomRecordWriter;
  /** Injected so the decision can be tested without waiting two minutes. */
  now?: () => number;
}

/**
 * Asks whether a code opens a room.
 *
 * `unknown` is the honest answer when the question could not be put — offline,
 * refused, malformed — and the caller lets the visitor in on it.
 */
export async function lookupRoomAccess({
  code,
  writer = defaultRoomRecordWriter(),
  now = Date.now,
}: LookupRoomOptions): Promise<RoomAccess> {
  const trimmed = normalise(code);
  if (!trimmed) return "closed";
  try {
    const { data, error } = await writer.lookup(trimmed);
    if (error) return "unknown";
    return roomAccessFor(toRecord(data), now());
  } catch {
    return "unknown";
  }
}

/**
 * Whether the signed-in account owns this room. The public lookup never returns
 * another account's identifier; a missing row and a failed read both remain
 * unknown so a local creation hint can preserve host continuity offline.
 */
export async function lookupRoomOwnership({
  code,
  writer = defaultRoomRecordWriter(),
}: LookupRoomOptions): Promise<boolean | null> {
  const trimmed = normalise(code);
  if (!trimmed) return null;
  try {
    const { data, error } = await writer.lookup(trimmed);
    if (error) return null;
    return data ? data.is_owner === true : null;
  } catch {
    return null;
  }
}

/**
 * The peer the room's owner is standing in it as, or null when there is none to
 * be had — nobody has claimed it this session, the code is unrecorded, or the
 * read could not be made.
 *
 * Every one of those means the same thing to the election: no owner is present,
 * and the earliest joiner hosts. A value that is not a peer id is one of them,
 * which is why anything unusable comes back as null rather than as itself.
 */
export async function lookupRoomHostPeer({
  code,
  writer = defaultRoomRecordWriter(),
}: LookupRoomOptions): Promise<string | null> {
  const trimmed = normalise(code);
  if (!trimmed) return null;
  try {
    const { data, error } = await writer.lookup(trimmed);
    if (error) return null;
    const peer = data?.host_peer_id;
    return typeof peer === "string" && peer.length > 0 ? peer : null;
  } catch {
    return null;
  }
}

/**
 * Says which peer the owner is standing in the room as. Only the account that
 * created the room can make this write land: the row's policy holds that, not
 * this call, so an attempt by anybody else is refused by the database rather
 * than talked out of it here.
 *
 * Best effort, like every other write to this row. A room whose owner is in it
 * but unrecorded elects on join time, which is a room with the wrong host and
 * not a room nobody can enter.
 */
export async function saveRoomHostPeer(
  code: string,
  peerId: string | null,
  writer: RoomRecordWriter = defaultRoomRecordWriter(),
): Promise<RoomRecordOutcome> {
  const trimmed = normalise(code);
  if (!trimmed) return "not-attempted";
  try {
    const { error } = await writer
      .from("rooms")
      .update({ host_peer_id: peerId })
      .eq("code", trimmed);
    return error ? "refused" : "recorded";
  } catch {
    return "refused";
  }
}

/**
 * How a code is configured, before anybody in it says so: what kind of room it
 * is, what it is called, and whether its tour starts by itself.
 *
 * One read rather than one per field — two reads are two answers that can
 * disagree — and the defaults for anything that cannot be read, which
 * `roomConfigFromRow` decides. A configuration never refuses anybody: a room
 * greeted by the wrong name is a room, and a room nobody can enter because its
 * name would not load is not.
 */
export async function lookupRoomConfig({
  code,
  writer = defaultRoomRecordWriter(),
}: LookupRoomOptions): Promise<RoomConfig> {
  const trimmed = normalise(code);
  if (!trimmed) return DEFAULT_ROOM_CONFIG;
  try {
    const { data, error } = await writer.lookup(trimmed);
    if (error) return DEFAULT_ROOM_CONFIG;
    return roomConfigFromRow(data);
  } catch {
    return DEFAULT_ROOM_CONFIG;
  }
}

/**
 * The room's settings, or nothing when the row could not be read.
 *
 * Its sibling above answers a configuration whatever happens, because a room
 * greeted by the wrong name is still a room to walk into. This one is the
 * opposite question and needs the opposite answer: since #612 a client *becomes*
 * what it reads here, so a read that failed must be silence rather than the
 * defaults — a hiccup would otherwise turn every room into a campfire on the
 * mesh.
 *
 * Asked without the visitor's session: every participant repeats this read on
 * an interval, the settings are the same whoever asks, and a session would cost
 * the route an Auth round trip per read for nothing.
 */
export async function readRoomSettings({
  code,
  writer = defaultRoomRecordWriter(),
}: LookupRoomOptions): Promise<RoomConfig | null> {
  const trimmed = normalise(code);
  if (!trimmed) return null;
  try {
    const { data, error } = await writer.lookup(trimmed, { asAccount: false });
    if (error || !data) return null;
    return roomConfigFromRow(data);
  } catch {
    return null;
  }
}

/**
 * Remembers what the host decided about the room, in the columns the row keeps
 * it in.
 *
 * One writer for every room-owned setting rather than one per setting: a second
 * near-identical update is where the next one is written without its own
 * fallback, and `roomConfigRowPatch` is already the single place that says how
 * each is spelled.
 *
 * Best effort, and reported the way `recordRoom` is: a change that reached
 * every participant has changed the room for as long as somebody is standing in
 * it, whether or not the note about it landed — and then loses it the moment the
 * room empties. The row's own policy, not this call, is what holds that only
 * the owner may write it.
 */
export async function saveRoomSettings(
  code: string,
  columns: RoomConfigColumns & Record<string, unknown>,
  writer: RoomRecordWriter = defaultRoomRecordWriter(),
): Promise<RoomRecordOutcome> {
  const trimmed = normalise(code);
  if (!trimmed) return "not-attempted";
  if (Object.keys(columns).length === 0) return "not-attempted";
  try {
    // Row-level security refuses an update by matching nothing rather than by
    // failing, so a write that touched no row is a refusal too.
    const { error, count } = await writer
      .from("rooms")
      .update(columns, { count: "exact" })
      .eq("code", trimmed);
    return error || count === 0 ? "refused" : "recorded";
  } catch {
    return "refused";
  }
}

function toRecord(row: RoomRow | null): RoomRecord | null {
  if (!row) return null;
  const lastSeen = row.last_seen_at ? Date.parse(row.last_seen_at) : Number.NaN;
  return {
    code: row.code,
    isPermanent: row.is_permanent === true,
    accountOwned: row.account_owned === true,
    // A row whose timestamp cannot be read is treated as cold rather than as
    // eternally fresh: the permanent flag is the only way to never expire.
    lastSeenAt: Number.isNaN(lastSeen) ? 0 : lastSeen,
  };
}

/**
 * Reports that somebody is still standing in the room. Best effort and silent:
 * the people inside must never learn that a heartbeat missed.
 */
export async function touchRoom(
  code: string,
  writer: RoomRecordWriter = defaultRoomRecordWriter(),
): Promise<boolean> {
  const trimmed = normalise(code);
  if (!trimmed) return false;
  try {
    const { error } = await writer.rpc("touch_room", { _code: trimmed });
    return !error;
  } catch {
    return false;
  }
}

export interface ReleaseRoomOptions {
  code: string;
  writer?: RoomRecordWriter;
}

/** Whether the room is gone, as the caller needs to see it. */
export type RoomReleaseOutcome = "released" | "not-attempted" | "refused";

/**
 * Gives up a room the account holds, so its code stops opening and the row
 * stops counting against the cap.
 *
 * Which rooms may go is the policy's to decide and not this function's: it asks
 * for the delete and reports what came back. An account that asks for somebody
 * else's room deletes nothing and is told it was refused, which is the same
 * answer it would get for a code that never existed — the two are deliberately
 * not distinguished here, because telling them apart tells a stranger which
 * codes are real. The policy filters such a row out rather than failing the
 * statement, so the delete is counted: no row removed is the refusal.
 */
export async function releaseRoom({
  code,
  writer = defaultRoomRecordWriter(),
}: ReleaseRoomOptions): Promise<RoomReleaseOutcome> {
  const trimmed = normalise(code);
  if (!trimmed) return "not-attempted";
  try {
    const { error, count } = await writer
      .from("rooms")
      .delete({ count: "exact" })
      .eq("code", trimmed);
    return error || !count ? "refused" : "released";
  } catch {
    return "refused";
  }
}

/**
 * How many of an account's rooms are asked for.
 *
 * The cap on what an account may hold is twenty, so this is the whole of it
 * with room to spare rather than a page: a list somebody scrolls is not a list
 * of their own rooms, and if it ever becomes one the cap is the thing to look
 * at first.
 */
export const ACCOUNT_ROOMS_LISTED = 24;

export interface ListAccountRoomsOptions {
  /** The account whose rooms these are. Nobody else's are ever asked for. */
  ownerId: string;
  writer?: RoomRecordWriter;
}

/** A room an account holds, as a list needs to see one. */
export interface AccountRoom {
  code: string;
  /** When somebody was last standing in it, or when it was recorded. */
  at: number;
  /** The name it was given when it was lit, where it was given one. */
  name?: string;
  /** What kind of room it is, where the row says so legibly. */
  mode?: RoomMode;
}

/**
 * The rooms an account created, most recently used first. The query names the
 * account for clarity and the database independently restricts the rows to the
 * signed-in owner.
 *
 * A read that fails is an empty list rather than a throw. The list is an
 * offer — a way back into a room without retyping its code — and a backend
 * hiccup must take the offer away, not the screen.
 */
export async function listAccountRooms({
  ownerId,
  writer = defaultRoomRecordWriter(),
}: ListAccountRoomsOptions): Promise<AccountRoom[]> {
  if (!ownerId) return [];
  try {
    const { data, error } = await writer
      .from("rooms")
      .select("code, last_seen_at, display_name, room_mode")
      .eq("created_by", ownerId)
      .order("last_seen_at", { ascending: false, nullsFirst: false })
      .limit(ACCOUNT_ROOMS_LISTED);
    if (error || !data) return [];
    return data.map((row) => {
      const seen = row.last_seen_at ? Date.parse(row.last_seen_at) : Number.NaN;
      // A row whose timestamp cannot be read sorts last rather than first: a
      // date nobody can parse is not news about when anybody was there.
      const mode = RoomModeSchema.safeParse(row.room_mode);
      return {
        code: row.code,
        at: Number.isNaN(seen) ? 0 : seen,
        name: row.display_name?.trim() || undefined,
        mode: mode.success ? mode.data : undefined,
      };
    });
  } catch {
    return [];
  }
}
